import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'semantic-ui-react';

import Icon from '@plone/volto/components/theme/Icon/Icon';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';

const normalizeValue = (v) => (v === '' || v === null ? undefined : v);

const TextWidget = (props) => {
  const {
    id,
    value,
    onChange,
    onBlur,
    onClick,
    icon,
    iconAction,
    minLength,
    maxLength,
    placeholder,
    isDisabled,
    focus,
  } = props;

  const ref = useRef();
  const composingRef = useRef(false);
  const [localValue, setLocalValue] = useState(value || '');

  useEffect(() => {
    if (!composingRef.current) {
      setLocalValue(value || '');
    }
  }, [value]);

  useEffect(() => {
    if (focus) {
      ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commitValue = (nextValue) => {
    onChange(id, normalizeValue(nextValue));
  };

  return (
    <FormFieldWrapper {...props} className="text">
      <Input
        id={`field-${id}`}
        name={id}
        value={localValue}
        disabled={isDisabled}
        icon={icon || null}
        placeholder={placeholder}
        onCompositionStart={() => {
          composingRef.current = true;
        }}
        onCompositionEnd={({ target }) => {
          composingRef.current = false;
          const next = target.value || '';
          setLocalValue(next);
          commitValue(next);
        }}
        onChange={({ target }) => {
          const next = target.value || '';
          setLocalValue(next);
          if (!composingRef.current) {
            commitValue(next);
          }
        }}
        ref={ref}
        onBlur={({ target }) => {
          const next = target.value || '';
          onBlur(id, normalizeValue(next));
          if (composingRef.current) {
            composingRef.current = false;
            commitValue(next);
          }
        }}
        onClick={() => onClick()}
        minLength={minLength || null}
        maxLength={maxLength || null}
      />
      {icon && iconAction && (
        <button className={`field-${id}-action-button`} onClick={iconAction}>
          <Icon name={icon} size="18px" />
        </button>
      )}
    </FormFieldWrapper>
  );
};

export default TextWidget;

TextWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  focus: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  icon: PropTypes.shape({
    xmlns: PropTypes.string,
    viewBox: PropTypes.string,
    content: PropTypes.string,
  }),
  iconAction: PropTypes.func,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  wrapped: PropTypes.bool,
  placeholder: PropTypes.string,
};

TextWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  onChange: () => {},
  onBlur: () => {},
  onClick: () => {},
  onEdit: null,
  onDelete: null,
  focus: false,
  icon: null,
  iconAction: null,
  minLength: null,
  maxLength: null,
};
